import { NEUTRAL } from './designTokens';
const BLOCK_LABELS = {
  video:   'Video',
  step:    'Steg',
  gallery: 'Bildgalleri',
  tip:     'Tips',
};

const TONE_LABELS = {
  info:    'Info',
  warning: 'Obs!',
  success: 'Bra att veta',
};

function trunc(str, max = 80) {
  if (!str) return '';
  return str.length > max ? str.slice(0, max) + '…' : str;
}

function TypeChip({ label, theme }) {
  return (
    <span style={{
      display: 'inline-block',
      background: theme.accent_soft_bg,
      color: theme.accent_soft_text,
      fontSize: '0.72em',
      fontWeight: '600',
      padding: '2px 8px',
      borderRadius: '4px',
      flexShrink: 0,
      whiteSpace: 'nowrap',
      minWidth: '72px',
      textAlign: 'center',
    }}>
      {label}
    </span>
  );
}

function BlockRow({ children, isLast }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '7px 0',
      borderBottom: isLast ? 'none' : `1px solid ${NEUTRAL.line}`,
    }}>
      {children}
    </div>
  );
}

function BlockContent({ children }) {
  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      {children}
    </div>
  );
}

function VideoBlockRow({ block, theme, isLast }) {
  const detail = block.video_label
    ? block.video_label
    : trunc(block.video_url, 60);
  return (
    <BlockRow isLast={isLast}>
      <TypeChip label="Video" theme={theme} />
      <BlockContent>
        <span style={{ fontSize: '0.82em', color: NEUTRAL.text }}>▶</span>
        {detail && (
          <span style={{ fontSize: '0.82em', color: NEUTRAL.muted }}>{detail}</span>
        )}
      </BlockContent>
    </BlockRow>
  );
}

function StepBlockRow({ block, stepNumber, theme, isLast }) {
  return (
    <BlockRow isLast={isLast}>
      <TypeChip label="Steg" theme={theme} />
      <BlockContent>
        <span style={{ fontSize: '0.75em', color: NEUTRAL.faint, fontWeight: '600', flexShrink: 0 }}>[{stepNumber}]</span>
        {block.title && (
          <span style={{ fontSize: '0.82em', color: NEUTRAL.text, fontWeight: '500' }}>{trunc(block.title, 40)}</span>
        )}
        {block.body && (
          <span style={{ fontSize: '0.82em', color: NEUTRAL.muted }}>{trunc(block.body, block.title ? 60 : 80)}</span>
        )}
        {block.images?.length > 0 && (
          <span style={{ fontSize: '0.75em', color: NEUTRAL.faint }}>+{block.images.length} bild{block.images.length !== 1 ? 'er' : ''}</span>
        )}
      </BlockContent>
    </BlockRow>
  );
}

function GalleryBlockRow({ block, theme, isLast }) {
  const count = block.images?.length || 0;
  const thumbs = (block.images || []).slice(0, 3);
  return (
    <BlockRow isLast={isLast}>
      <TypeChip label="Bildgalleri" theme={theme} />
      <BlockContent>
        <span style={{ fontSize: '0.82em', color: NEUTRAL.muted }}>{count} bild{count !== 1 ? 'er' : ''}</span>
        {thumbs.map(img => (
          <img
            key={img.id}
            src={img.url}
            alt={img.alt || ''}
            style={{ width: '36px', height: '36px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }}
            onError={e => { e.currentTarget.style.display = 'none'; }}
          />
        ))}
      </BlockContent>
    </BlockRow>
  );
}

function TipBlockRow({ block, theme, isLast }) {
  const toneLabel = TONE_LABELS[block.tone] || 'Info';
  return (
    <BlockRow isLast={isLast}>
      <TypeChip label="Tips" theme={theme} />
      <BlockContent>
        <span style={{ fontSize: '0.75em', color: NEUTRAL.faint, flexShrink: 0 }}>{toneLabel}</span>
        {block.body && (
          <span style={{ fontSize: '0.82em', color: NEUTRAL.muted }}>{trunc(block.body)}</span>
        )}
      </BlockContent>
    </BlockRow>
  );
}

function UnknownBlockRow({ block, theme, isLast }) {
  return (
    <BlockRow isLast={isLast}>
      <TypeChip label={BLOCK_LABELS[block.type] || block.type || '?'} theme={theme} />
      <BlockContent>
        <span style={{ fontSize: '0.82em', color: NEUTRAL.faint }}>Block (okänd typ)</span>
      </BlockContent>
    </BlockRow>
  );
}

export default function ExerciseBlockPreview({ blocks, theme }) {
  if (!blocks?.length) return null;

  const sorted = [...blocks].sort((a, b) => a.order - b.order);
  let stepCounter = 0;

  return (
    <div style={{
      background: NEUTRAL.page,
      borderRadius: '8px',
      padding: '2px 12px 2px',
      marginBottom: '12px',
    }}>
      {sorted.map((block, i) => {
        const isLast = i === sorted.length - 1;
        switch (block.type) {
          case 'video':
            return <VideoBlockRow key={block.id} block={block} theme={theme} isLast={isLast} />;
          case 'step':
            stepCounter += 1;
            return <StepBlockRow key={block.id} block={block} stepNumber={stepCounter} theme={theme} isLast={isLast} />;
          case 'gallery':
            return <GalleryBlockRow key={block.id} block={block} theme={theme} isLast={isLast} />;
          case 'tip':
            return <TipBlockRow key={block.id} block={block} theme={theme} isLast={isLast} />;
          default:
            return <UnknownBlockRow key={block.id || i} block={block} theme={theme} isLast={isLast} />;
        }
      })}
    </div>
  );
}
